// ─── Choices.js for SortBy ────────────────────────────────────────────────────
// Guard against double-initialisation (e.g. when the script is injected more
// than once via section renders).  Re-use an existing instance when found.
let _sortChoices = null;
(function () {
  const el = document.getElementById('SortBy');
  if (!el) return;
  if (el._choicesInstance) { _sortChoices = el._choicesInstance; return; }
  _sortChoices = new Choices(el, { searchEnabled: false, itemSelectText: '', allowHTML: true });
  el._choicesInstance = _sortChoices;
}());

// ─── Core: Shopify section-render AJAX ───────────────────────────────────────
const MatatFilter = {
  _controller: null,
  searchParamsInitial: window.location.search.slice(1),

  renderPage(searchParams, pushState = true) {
    const sectionId = document.getElementById('product-grid')?.dataset.id;
    if (!sectionId) return;

    // Cancel any previous in-flight request.
    this._controller?.abort();
    this._controller = new AbortController();

    // Signal to assistive technology that the region is updating.
    const grid = document.getElementById('ProductGridContainer');
    grid?.setAttribute('aria-busy', 'true');
    grid?.querySelector('.collection')?.classList.add('loading');

    fetch(
      `${window.location.pathname}?section_id=${sectionId}&${searchParams}`,
      { signal: this._controller.signal }
    )
      .then(r => r.text())
      .then(html => {
        this._controller = null;

        // menu-drawer's onCloseButtonClick calls closeSubmenu (not closeMenuDrawer),
        // so the scroll-lock body class is never removed via the normal close path.
        document.body.classList.remove(
          'overflow-hidden',
          'overflow-hidden-filter',
          'overflow-hidden-undefined',
          'overflow-hidden-tablet',
          'overflow-hidden-mobile'
        );

        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Patch each filter panel individually so <details> open-state is preserved.
        doc.querySelectorAll('#MatatFiltersForm .js-filter').forEach(newEl => {
          const cur = document.querySelector(
            `#MatatFiltersForm .js-filter[data-index="${newEl.dataset.index}"]`
          );
          if (cur) cur.innerHTML = newEl.innerHTML;
        });

        // Patch product grid.
        const newGrid = doc.getElementById('ProductGridContainer');
        if (newGrid && grid) {
          grid.innerHTML = newGrid.innerHTML;
        }

        // Always clear loading state — even when the response omits
        // #ProductGridContainer (e.g. a search with no q param).
        grid?.querySelector('.collection')?.classList.remove('loading');
        grid?.setAttribute('aria-busy', 'false');

        // Patch product count and announce the change to screen readers.
        const newCount = doc.getElementById('ProductCount');
        const countEl  = document.getElementById('ProductCount');
        if (newCount && countEl) countEl.innerHTML = newCount.innerHTML;

        if (pushState) {
          history.pushState(
            { searchParams },
            '',
            window.location.pathname + (searchParams ? `?${searchParams}` : '')
          );
        }

        MatatFilter.initPriceSlider();
        MatatFilter.autoSelectFilteredColors(searchParams);
        if (typeof initProductCardSwipers === 'function') initProductCardSwipers();
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        if (window.Shopify && window.Shopify.designMode) console.error('[MatatFilter]', err);
        grid?.querySelector('.collection')?.classList.remove('loading');
        grid?.setAttribute('aria-busy', 'false');
      });
  },

  buildParams(form) {
    const params = new URLSearchParams(new FormData(form));

    // Drop price bounds that equal the slider's absolute min/max so the URL
    // stays clean and Shopify doesn't over-filter.
    const slider = document.getElementById('matat-range-slider');
    if (slider) {
      const absMin = slider.getAttribute('min') ?? '0';
      const absMax = slider.getAttribute('max');
      if (params.get('filter.v.price.gte') === absMin) params.delete('filter.v.price.gte');
      if (absMax && params.get('filter.v.price.lte') === absMax) params.delete('filter.v.price.lte');
    }

    // Preserve sort, search query, and result-type across filter changes.
    const current = new URLSearchParams(window.location.search);
    ['sort_by', 'q', 'type'].forEach(key => {
      const val = current.get(key);
      if (val) params.set(key, val);
    });

    return params.toString();
  },

  initPriceSlider() {
    const slider = document.getElementById('matat-range-slider');
    if (!slider) return;

    // Destroy the previous Rangeable instance before re-initialising so event
    // listeners don't stack up across filter renders.
    if (slider._rangeable) {
      try { slider._rangeable.destroy(); } catch (_) {}
      slider._rangeable = null;
    }

    const isSidebar = !!document.querySelector('.matat-filter-layout-sidebar');

    slider._rangeable = new Rangeable(slider, {
      type:     'double',
      tooltips: false,
      step:     1,
      value:    [slider.dataset.min, slider.dataset.max],

      onInit(val) {
        document.getElementById('Filter-Price-GTE').value      = val[0];
        document.getElementById('Filter-Price-LTE').value      = val[1];
        document.getElementById('selectedPriceMin').textContent = val[0];
        document.getElementById('selectedPriceMax').textContent = val[1];
      },

      // onChange: update display values only.
      // Rangeable registers a document.scroll listener internally and calls
      // onChange on every page scroll — applying the filter here would fire
      // renderPage on every scroll event.
      onChange(val) {
        document.getElementById('Filter-Price-GTE').value      = val[0];
        document.getElementById('Filter-Price-LTE').value      = val[1];
        document.getElementById('selectedPriceMin').textContent = val[0];
        document.getElementById('selectedPriceMax').textContent = val[1];
      },

      // onEnd fires only when the user releases the handle (mouseup / touchend),
      // not from the scroll listener — safe to apply the filter here.
      onEnd() {
        if (!isSidebar) return;
        const form = document.getElementById('MatatFiltersForm');
        if (form) MatatFilter.renderPage(MatatFilter.buildParams(form));
      },
    });
  },

  /**
   * After a colour filter is applied, find the matching colour swatch on each
   * product card and click it so the card image reflects the filtered colour.
   * @param {string} [searchParams] - defaults to the current URL search string.
   */
  autoSelectFilteredColors(searchParams) {
    const params = new URLSearchParams(searchParams ?? window.location.search);
    const colors = new Set();

    params.forEach((val, key) => {
      if (/^filter\..+\.color$/i.test(key)) colors.add(val.toLowerCase());
    });

    if (!colors.size) return;

    document.querySelectorAll('.product-card-wrapper').forEach(card => {
      for (const input of card.querySelectorAll('input.custom_variation_button')) {
        if (colors.has(input.value.toLowerCase()) && !input.disabled && !input.checked) {
          input.click();
          break; // one colour per card is enough
        }
      }
    });
  },
};

// ─── matat-filters-form custom element ───────────────────────────────────────
class MatatFiltersForm extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const form = this.querySelector('form');
    if (!form) return;

    const isSidebarLayout = !!document.querySelector('.matat-filter-layout-sidebar');
    // Mobile always uses drawer behaviour regardless of the layout setting.
    const isSidebar = isSidebarLayout && window.innerWidth > 989;

    // On mobile with sidebar layout the <details> starts with `open` for
    // desktop; remove it so the first tap opens rather than closes the drawer.
    if (isSidebarLayout && !isSidebar) {
      const details = this.querySelector('details');
      details?.removeAttribute('open');
      details?.classList.remove('menu-opening');
    }

    // Sort-by dropdown — bind once with a guard flag.
    const sortEl = document.getElementById('SortBy');
    if (sortEl && !sortEl._matatBound) {
      sortEl._matatBound = true;
      sortEl.addEventListener('change', () => {
        const params = new URLSearchParams(window.location.search);
        params.set('sort_by', sortEl.value);
        MatatFilter.renderPage(params.toString());
      });
    }

    if (isSidebar) {
      // Desktop sidebar: auto-apply every checkbox change.
      form.addEventListener('change', e => {
        if (e.target.type !== 'checkbox') return;
        MatatFilter.renderPage(MatatFilter.buildParams(form));
      });
    } else {
      // Drawer / mobile: Apply button triggers render.
      form.querySelector('button.button')?.addEventListener('click', e => {
        e.preventDefault();
        MatatFilter.renderPage(MatatFilter.buildParams(form));
      });
      form.querySelector('.matat-filters__close')?.addEventListener('click', () => {
        form.querySelector('.filter-opener')?.dispatchEvent(new Event('click'));
      });
    }

    // Scroll-lock: menu-drawer only adds overflow-hidden-tablet (no effect on
    // desktop) so we manage overflow-hidden ourselves for all screen sizes.
    const filterDetails = this.querySelector('details');
    const filterOpener  = this.querySelector('summary.filter-opener');
    if (filterOpener && filterDetails && !isSidebar) {
      filterOpener.addEventListener('click', () => {
        document.body.classList.toggle(
          'overflow-hidden',
          !filterDetails.hasAttribute('open')
        );
      });
    }

    document.getElementById('MatatFiltersForm')?.classList.add('loaded');
    document.getElementById('ProductGridContainer')?.classList.add('loaded');
    MatatFilter.initPriceSlider();
  }
}
customElements.define('matat-filters-form', MatatFiltersForm);

// ─── matat-filter-remove custom element ──────────────────────────────────────
class MatatFilterRemove extends HTMLElement {
  connectedCallback() {
    const link = this.querySelector('a');
    if (!link || link._matatBound) return;
    link._matatBound = true;
    link.setAttribute('role', 'button');

    link.addEventListener('click', e => {
      e.preventDefault();
      const rawParams = new URL(link.href, window.location.href).search.slice(1);
      const params    = new URLSearchParams(rawParams);

      // Preserve search query and type so clearing a filter on a search page
      // doesn't drop the search context and cause an infinite spinner.
      const current = new URLSearchParams(window.location.search);
      ['q', 'type'].forEach(key => {
        const val = current.get(key);
        if (val) params.set(key, val);
      });

      MatatFilter.renderPage(params.toString());

      // Close the drawer after clearing — except on desktop sidebar where
      // the panel is always visible.
      const isDesktopSidebar =
        window.innerWidth > 989 &&
        !!document.querySelector('.matat-filter-layout-sidebar');
      if (!isDesktopSidebar) {
        const opener  = document.querySelector('summary.filter-opener');
        const details = opener?.closest('details');
        if (details?.hasAttribute('open')) opener.click();
      }
    });
  }
}
customElements.define('matat-filter-remove', MatatFilterRemove);

// ─── price-range custom element (manual text inputs) ─────────────────────────
class PriceRange extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('input:not([type="range"])').forEach(el => {
      el.addEventListener('change', () => {
        this._clamp(el);
        this._syncBounds();
      });
    });
    this._syncBounds();
  }

  _syncBounds() {
    const [min, max] = [...this.querySelectorAll('input')];
    if (!min || !max) return;
    if (max.value) min.setAttribute('max', max.value);
    if (min.value) max.setAttribute('min', min.value);
    if (!min.value) max.setAttribute('min', '0');
    if (!max.value) min.setAttribute('max', min.getAttribute('max'));
  }

  _clamp(input) {
    const v  = Number(input.value);
    const lo = Number(input.getAttribute('min'));
    const hi = Number(input.getAttribute('max'));
    if (v < lo) input.value = lo;
    if (v > hi) input.value = hi;
  }
}
customElements.define('price-range', PriceRange);

// ─── Back / forward navigation ────────────────────────────────────────────────
window.addEventListener('popstate', e => {
  MatatFilter.renderPage(e.state?.searchParams ?? MatatFilter.searchParamsInitial, false);
});

// ─── Close sort dropdown on scroll ───────────────────────────────────────────
window.addEventListener('scroll', () => _sortChoices?.hideDropdown(), { passive: true });

// ─── Re-init price slider when the Price accordion opens ─────────────────────
// 100 ms gives the <details> open-transition time to finish before Rangeable
// measures the track width; 50 ms was too tight on lower-end hardware.
document.addEventListener('click', e => {
  const summary = e.target.closest('summary.matat_filters__summary');
  if (summary?.closest('details')?.querySelector('#matat-range-slider')) {
    setTimeout(() => MatatFilter.initPriceSlider(), 100);
  }
});

// ─── Auto-select filtered colour on initial page load ────────────────────────
// Handles bookmarks, back-navigation, and page refresh after filtering.
document.addEventListener('DOMContentLoaded', () => {
  // Mark the product count as a live region so screen readers announce
  // when the number changes after a filter is applied.
  document.getElementById('ProductCount')?.setAttribute('aria-live', 'polite');
  MatatFilter.autoSelectFilteredColors();
});
